import dayjs, { type Dayjs } from 'dayjs';
import 'dayjs/locale/zh-cn.js';
import customParseFormat from 'dayjs/plugin/customParseFormat.js';
import timezone from 'dayjs/plugin/timezone.js';
import utc from 'dayjs/plugin/utc.js';

dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale('zh-cn');

export const BUSINESS_TIME_ZONE = 'Asia/Shanghai';
export const NATURAL_DATE_FORMAT = 'YYYY-MM-DD';
export const BUSINESS_DATE_TIME_FORMAT = 'YYYY-MM-DD HH:mm';

export type BusinessDateMode = 'date' | 'datetime';
export type BusinessDateRangeValue = readonly [string | undefined, string | undefined];

export function formatShanghaiDateTime(value: unknown): string {
  if (typeof value !== 'string') return '-';
  return parseBusinessDateTime(value)?.format(BUSINESS_DATE_TIME_FORMAT) ?? '-';
}

export function parseNaturalDate(value: string | null | undefined): Dayjs | undefined {
  if (!value) return undefined;
  const parsed = dayjs(value, NATURAL_DATE_FORMAT, true);
  return parsed.isValid() ? parsed : undefined;
}

export function normalizeNaturalDate(value: string | null | undefined): string | undefined {
  return parseNaturalDate(value)?.format(NATURAL_DATE_FORMAT);
}

export function parseBusinessDateTime(value: string | null | undefined): Dayjs | undefined {
  if (!value) return undefined;
  const match =
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.\d{1,3})?)?(?:Z|[+-](?:(?:0\d|1[0-3]):[0-5]\d|14:00))$/u.exec(
      value,
    );
  if (!match) {
    return undefined;
  }
  const [, year, month, day, hour, minute, matchedSecond] = match;
  if (!year || !month || !day || !hour || !minute) return undefined;
  const second = matchedSecond ?? '00';
  const wallClock = `${year}-${month}-${day} ${hour}:${minute}:${second}`;
  if (!dayjs(wallClock, 'YYYY-MM-DD HH:mm:ss', true).isValid()) return undefined;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.tz(BUSINESS_TIME_ZONE) : undefined;
}

export function normalizeBusinessDateTime(value: string | null | undefined): string | undefined {
  return parseBusinessDateTime(value)?.second(0).millisecond(0).toISOString();
}

export function parseRangeValue(
  mode: BusinessDateMode,
  value: BusinessDateRangeValue,
): readonly [Dayjs | null, Dayjs | null] {
  const parse = mode === 'date' ? parseNaturalDate : parseBusinessDateTime;
  return [parse(value[0]) ?? null, parse(value[1]) ?? null];
}

export function serializeRangeValue(
  mode: BusinessDateMode,
  value: readonly [Dayjs | null, Dayjs | null],
): BusinessDateRangeValue {
  return [serializeDate(mode, value[0]), serializeDate(mode, value[1])];
}

export function isReversedRange(value: readonly [Dayjs | null, Dayjs | null]): boolean {
  const [from, to] = value;
  return from !== null && to !== null && from.valueOf() > to.valueOf();
}

export function currentShanghaiMonth(now = new Date()): BusinessDateRangeValue {
  const current = dayjs(now).tz(BUSINESS_TIME_ZONE);
  return [
    current.startOf('month').format(NATURAL_DATE_FORMAT),
    current.format(NATURAL_DATE_FORMAT),
  ];
}

export function previousShanghaiMonth(now = new Date()): BusinessDateRangeValue {
  const previous = dayjs(now).tz(BUSINESS_TIME_ZONE).subtract(1, 'month');
  return [
    previous.startOf('month').format(NATURAL_DATE_FORMAT),
    previous.endOf('month').format(NATURAL_DATE_FORMAT),
  ];
}

export function currentShanghaiWeek(now = new Date()): BusinessDateRangeValue {
  const current = dayjs(now).tz(BUSINESS_TIME_ZONE);
  const mondayOffset = (current.day() + 6) % 7;
  const start = current.subtract(mondayOffset, 'day').startOf('day');
  return [start.format(NATURAL_DATE_FORMAT), start.add(6, 'day').format(NATURAL_DATE_FORMAT)];
}

export function shanghaiDayStartIso(value: string): string {
  const parsed = requireNaturalDate(value);
  return dayjs
    .tz(parsed.format(NATURAL_DATE_FORMAT), NATURAL_DATE_FORMAT, BUSINESS_TIME_ZONE)
    .startOf('day')
    .toISOString();
}

export function shanghaiDayAfterIso(value: string): string {
  const parsed = requireNaturalDate(value);
  return dayjs
    .tz(parsed.format(NATURAL_DATE_FORMAT), NATURAL_DATE_FORMAT, BUSINESS_TIME_ZONE)
    .add(1, 'day')
    .startOf('day')
    .toISOString();
}

function serializeDate(mode: BusinessDateMode, value: Dayjs | null): string | undefined {
  if (value === null) return undefined;
  if (mode === 'date') return value.format(NATURAL_DATE_FORMAT);
  const wallClock = value.format(BUSINESS_DATE_TIME_FORMAT);
  return dayjs.tz(wallClock, BUSINESS_DATE_TIME_FORMAT, BUSINESS_TIME_ZONE).toISOString();
}

function requireNaturalDate(value: string): Dayjs {
  const parsed = parseNaturalDate(value);
  if (!parsed) throw new Error(`Invalid natural date: ${value}`);
  return parsed;
}
